import { useEffect, useMemo, useState } from "react";

export default function FlashTicker({ items = [], lang = "en", variant = "ticker" }) {
  const safe = Array.isArray(items) ? items.filter(Boolean) : [];
  if (safe.length === 0) return null;

  return variant === "slider" ? (
    <FlashSlider items={safe} lang={lang} />
  ) : (
    <FlashMarquee items={safe} lang={lang} />
  );
}

function truncate(s, max = 140) {
  const t = String(s || "").replace(/\s+/g, " ").trim();
  if (!t) return "";
  return t.length <= max ? t : `${t.slice(0, max - 1)}…`;
}

function pickText(m, lang) {
  // Your backend flash-messages returns: full_name, contribution_text, content_lang, person_slug, person_name_en/kn
  // Prefer contribution_text first
  const msg =
    m.contribution_text ||
    (lang === "kn" ? m.message_kn : m.message_en) ||
    (lang === "kn" ? m.text_kn : m.text_en) ||
    (lang === "kn" ? m.title_kn : m.title_en) ||
    m.message ||
    m.text ||
    m.title ||
    "";

  const name = (m.full_name || "").trim();

  // Optional: show person name if present
  const personName =
    (lang === "kn" ? m.person_name_kn : m.person_name_en) ||
    m.person_name_en ||
    m.person_name_kn ||
    "";

  const prefix = name ? `${name}: ` : personName ? `${personName}: ` : "";

  return truncate(prefix + msg, 160);
}

function FlashMarquee({ items, lang }) {
  const texts = useMemo(() => items.map((m) => pickText(m, lang)).filter(Boolean), [items, lang]);
  if (texts.length === 0) return null;

  // Duplicate for seamless loop
  const loop = [...texts, ...texts];

  return (
    <div style={S.wrap} role="region" aria-label="Flash messages">
      <style>{css}</style>
      <div style={S.badge}>FLASH</div>

      <div style={S.viewport}>
        <div className="flash-track">
          {loop.map((t, i) => (
            <div key={`${i}-${t.slice(0, 12)}`} style={S.item} title={t}>
              {t}
            </div>
          ))}
        </div>
      </div>

      <div style={S.hint}>{lang === "kn" ? "ನವೀಕರಣಗಳು" : "Updates"}</div>
    </div>
  );
}

function FlashSlider({ items, lang }) {
  const texts = useMemo(() => items.map((m) => pickText(m, lang)).filter(Boolean), [items, lang]);
  const [idx, setIdx] = useState(0);
  const len = texts.length;

  useEffect(() => {
    if (len <= 1) return;
    const t = setInterval(() => setIdx((v) => (v + 1) % len), 3500);
    return () => clearInterval(t);
  }, [len]);

  if (len === 0) return null;

  return (
    <div style={S.wrap} role="region" aria-label="Flash messages">
      <div style={S.badge}>FLASH</div>
      <div style={S.sliderBox}>
        <div style={S.sliderText} title={texts[idx]}>
          {texts[idx]}
        </div>
        {len > 1 && (
          <div style={S.dots}>
            {texts.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`flash-${i}`}
                style={{
                  ...S.dot,
                  opacity: i === idx ? 1 : 0.35,
                }}
              />
            ))}
          </div>
        )}
      </div>
      <div style={S.hint}>{lang === "kn" ? "ನವೀಕರಣಗಳು" : "Updates"}</div>
    </div>
  );
}

const css = `
.flash-track{
  display:flex;
  gap:14px;
  align-items:center;
  width:max-content;
  animation: flashScroll 22s linear infinite;
}
@keyframes flashScroll{
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
}
@media (max-width: 720px){
  .flash-track{ animation-duration: 28s; }
}
`;

const S = {
  wrap: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid #e7eaf0",
    background: "linear-gradient(90deg, rgba(13,71,161,0.10), rgba(255,255,255,1))",
    boxShadow: "0 8px 24px rgba(2,6,23,0.06)",
    marginBottom: 12,
  },
  badge: {
    fontWeight: 900,
    fontSize: 12,
    letterSpacing: "0.6px",
    padding: "6px 10px",
    borderRadius: 999,
    color: "#fff",
    background: "#b71c1c",
    flex: "0 0 auto",
  },
  viewport: {
    overflow: "hidden",
    flex: "1 1 auto",
    minWidth: 0,
  },
  item: {
    whiteSpace: "nowrap",
    fontSize: 14,
    fontWeight: 700,
    color: "#0f172a",
    padding: "6px 10px",
    borderRadius: 999,
    background: "rgba(255,255,255,0.85)",
    border: "1px solid rgba(2,6,23,0.06)",
    maxWidth: 520,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  hint: {
    fontSize: 12,
    fontWeight: 800,
    color: "#334155",
    flex: "0 0 auto",
  },
  sliderBox: {
    flex: "1 1 auto",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  sliderText: {
    fontSize: 14,
    fontWeight: 800,
    color: "#0f172a",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  dots: { display: "flex", gap: 6, alignItems: "center" },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 999,
    border: "1px solid rgba(2,6,23,0.18)",
    background: "#0d47a1",
    cursor: "pointer",
  },
};
